/**
 * Onboarding step registry — the ONE place the five Welcome‑Checklist steps are defined
 * (titles and one‑liners are Tim's, Beta‑1 feedback D02). Consumers:
 *   - WelcomeChecklist (dialog rows)          - Dashboard "Getting started" card
 *   - Help & Docs "Guided tours" cards        - tours.js keys (must match `key`)
 *
 * Keys are stored in Hugo_Onboarding.steps.{key}; never rename a key without a migration.
 * `barometer` was removed in Beta‑1 (the Barometer moves to the HUGO assistant); an old
 * `steps.barometer: true` in Firestore is simply ignored.
 */
import React from 'react';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { ONBOARDING_STEPS } from './onboardingEvents';

const DA = '/drew';

export const ONBOARDING_STEP_DEFS = [
  {
    key: 'find-the-fire',
    guide: '/guide#b-drew-dashboard', // P8-01: matching User Guide section
    title: 'Find the Fire',
    description: 'Filter by different sites until you discover red indications',
    blurb: 'Company-wide looks fine. Filter by site and find what the average is hiding.',
    minutes: 3,
    route: DA,
    Icon: WhatshotIcon,
    color: 'error',
  },
  {
    key: 'read-the-alert',
    guide: '/guide#c-learning-the-system', // P8-01: matching User Guide section
    title: 'Read Alerts Like a Pro',
    description: 'Open the critical risk alert and its Reverse/Eliminate/Fortify recommendations',
    blurb: "The anatomy of a risk alert: what's accumulating, why it matters, what to do.",
    minutes: 2,
    route: DA,
    Icon: CrisisAlertIcon,
    color: 'warning',
  },
  {
    key: 'move-the-numbers',
    guide: '/guide#c-learning-the-system', // P8-01: matching User Guide section
    title: 'Drift & Accumulation Perspectives',
    description: "The Drift & Accumulation Survey accesses and assesses team members' input regarding organizational entropy",
    blurb: 'Take a survey and watch scores and warnings react live.',
    minutes: 3,
    route: `${DA}/surveys`,
    Icon: TrendingUpIcon,
    color: 'success',
  },
  {
    key: 'add-your-people',
    guide: '/guide#c-learning-the-system', // P8-01: matching User Guide section
    title: 'Identify & Assign REPSit.ai Users',
    description: 'Add team members and assign their roles — then use View As to see the app through their eyes.',
    blurb: 'Roles scoped to their slice, then View As to see the app through their eyes.',
    minutes: 2,
    route: '/settings/organization',
    Icon: GroupAddIcon,
    color: 'secondary',
  },
  {
    key: 'import-your-org',
    guide: '/guide#c-learning-the-system', // P8-01: matching User Guide section
    title: 'Set Up Your Organization',
    description: "Upload your org chart, PDF, photo, or spreadsheet. Edit and approve REPSit.ai's proposed organization.",
    blurb: 'Turn a PDF, photo, or spreadsheet of your org chart into your real workspace.',
    minutes: 2,
    route: '/settings/organization',
    Icon: UploadFileIcon,
    color: 'info',
  },
];

// Guard: the key list in onboardingEvents.js and this registry must agree.
if (import.meta.env?.DEV) {
  const a = ONBOARDING_STEP_DEFS.map((s) => s.key).join(',');
  const b = ONBOARDING_STEPS.join(',');
  if (a !== b) console.warn(`[onboarding] step registry (${a}) ≠ ONBOARDING_STEPS (${b})`);
}

export const stepDef = (key) => ONBOARDING_STEP_DEFS.find((s) => s.key === key) || null;
export const stepTitle = (key) => stepDef(key)?.title || key;

/** Render a step's icon element (`<StepIcon step={def} />`). */
export function StepIcon({ step, ...props }) {
  const Icon = step?.Icon;
  return Icon ? <Icon color={step.color} {...props} /> : null;
}

export default ONBOARDING_STEP_DEFS;
